import { EARTH } from '@trajectory/astro';
import type { BodyIdDto, OrbitalElementsDto } from '@trajectory/contracts';
import { NumberField, SelectField } from '@trajectory/ui';
import { Loader2, Route, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { PanelLearningBanner } from '@/components/PanelLearningBanner';
import { TermHelp } from '@/components/TermHelp';
import { useReentryAnalysis, useTransferAnalysis } from '@/hooks/useAnalysis';
import { useMissionStore } from '@/stores/missionStore';

const TRANSFER_BODIES = ['moon', 'mars', 'venus', 'jupiter'] as const;
type TransferBody = (typeof TRANSFER_BODIES)[number];

const TRANSFER_BODY_LABELS: Record<TransferBody, string> = {
  moon: 'Moon',
  mars: 'Mars',
  venus: 'Venus',
  jupiter: 'Jupiter',
};

function isTransferBody(value: string): value is TransferBody {
  return (TRANSFER_BODIES as readonly string[]).includes(value);
}

function formatDeltaV(metersPerSecond: number): string {
  return `${(metersPerSecond / 1000).toFixed(2)} km/s`;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '—';
  const days = seconds / 86400;
  if (days >= 1) return `${days.toFixed(1)} d`;
  const hours = seconds / 3600;
  if (hours >= 1) return `${hours.toFixed(1)} h`;
  return `${(seconds / 60).toFixed(0)} min`;
}

const buttonClass =
  'flex items-center justify-center gap-2 rounded-control border border-border bg-surface-raised px-3 py-1.5 text-xs text-text hover:bg-surface-sunken disabled:opacity-40';

const pillClass = (active: boolean) =>
  `rounded-control border px-2 py-1 text-xs ${active ? 'border-accent text-accent' : 'border-border text-muted hover:text-text'}`;

function TransferSection() {
  const targetOrbit = useMissionStore((state) => state.document?.targetOrbit);
  const destinationBody = useMissionStore((state) => state.document?.destinationBody ?? 'earth');

  const [mode, setMode] = useState<'orbit' | 'body'>(() =>
    isTransferBody(destinationBody) ? 'body' : 'orbit',
  );
  const [fromAltitudeKm, setFromAltitudeKm] = useState(200);
  const [fromInclinationDeg, setFromInclinationDeg] = useState(targetOrbit?.inclinationDeg ?? 28.5);
  const [targetBody, setTargetBody] = useState<TransferBody>(
    isTransferBody(destinationBody) ? destinationBody : 'moon',
  );
  const transfer = useTransferAnalysis();

  const fromOrbit: OrbitalElementsDto = {
    semiMajorAxisMeters: EARTH.equatorialRadius + fromAltitudeKm * 1000,
    eccentricity: 0,
    inclinationDeg: fromInclinationDeg,
    raanDeg: 0,
    argumentOfPeriapsisDeg: 0,
    trueAnomalyDeg: 0,
  };

  const canCompute = mode === 'body' || Boolean(targetOrbit);

  const handleCompute = () => {
    if (!canCompute) return;
    transfer.mutate(
      mode === 'orbit' && targetOrbit
        ? { from: fromOrbit, to: targetOrbit, centralBody: 'earth' as const }
        : { from: fromOrbit, targetBody: targetBody as BodyIdDto, centralBody: 'earth' as const },
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-panel border border-border bg-surface-raised p-3 text-xs">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-muted">Transfer planner</h3>
        <div className="flex gap-1">
          <button type="button" onClick={() => setMode('orbit')} className={pillClass(mode === 'orbit')}>
            Orbit-to-orbit
          </button>
          <button type="button" onClick={() => setMode('body')} className={pillClass(mode === 'body')}>
            To body
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label={<TermHelp termId="altitude">Departure altitude (km)</TermHelp>}
          value={fromAltitudeKm}
          min={150}
          hint="The circular parking-orbit altitude used as the transfer starting point."
          onChange={setFromAltitudeKm}
        />
        <NumberField
          label={<TermHelp termId="inclination">Departure inclination (°)</TermHelp>}
          value={fromInclinationDeg}
          min={0}
          max={180}
          hint="Plane changes can dominate the required delta-v; match the real parking orbit."
          onChange={setFromInclinationDeg}
        />
      </div>

      {mode === 'body' ? (
        <SelectField
          label="Destination body"
          value={targetBody}
          options={TRANSFER_BODIES.map((body) => ({ value: body, label: TRANSFER_BODY_LABELS[body] }))}
          onChange={(value) => setTargetBody(value as TransferBody)}
        />
      ) : (
        !targetOrbit && <p className="text-faint">Set a target orbit in the Orbit Editor first.</p>
      )}

      <button
        type="button"
        onClick={handleCompute}
        disabled={!canCompute || transfer.isPending}
        title="Estimate transfer burns and time from the entered parking orbit; this does not change the mission"
        className={buttonClass}
      >
        {transfer.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Route className="h-3.5 w-3.5" />}
        Compute transfer
      </button>

      {transfer.isError && (
        <p role="alert" className="text-danger">{transfer.error instanceof Error ? transfer.error.message : String(transfer.error)}</p>
      )}

      {transfer.data && (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-1 text-muted">
            <span>Kind</span>
            <span className="text-right capitalize text-text">{transfer.data.kind.replace(/-/g, ' ')}</span>
            <span><TermHelp termId="delta-v">Total ΔV</TermHelp></span>
            <span className="text-right text-text">{formatDeltaV(transfer.data.totalDeltaVMetersPerSecond)}</span>
            <span>Transfer time</span>
            <span className="text-right text-text">{formatDuration(transfer.data.transferTimeSeconds)}</span>
            {transfer.data.c3 !== undefined && (
              <>
                <span><TermHelp termId="c3">C3 (escape energy)</TermHelp></span>
                <span className="text-right text-text">{(transfer.data.c3 / 1e6).toFixed(1)} km²/s²</span>
              </>
            )}
            {transfer.data.departurePhaseAngleDeg !== undefined && (
              <>
                <span><TermHelp termId="phase-angle">Departure phase angle</TermHelp></span>
                <span className="text-right text-text">{transfer.data.departurePhaseAngleDeg.toFixed(1)}°</span>
              </>
            )}
          </div>

          <div>
            <p className="mb-1 font-medium text-text">Burns</p>
            <div className="flex flex-col gap-0.5">
              {transfer.data.burns.map((burn, index) => (
                <div key={`${burn.label}-${index}`} className="flex items-center justify-between text-muted">
                  <span>{burn.label}</span>
                  <span className="text-text">{formatDeltaV(burn.deltaVMetersPerSecond)}</span>
                </div>
              ))}
            </div>
          </div>

          {transfer.data.notes.length > 0 && (
            <ul className="list-disc pl-4 text-faint">
              {transfer.data.notes.map((note, index) => (
                <li key={index}>{note}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ReentrySection() {
  const targetOrbit = useMissionStore((state) => state.document?.targetOrbit);
  const launchAt = useMissionStore((state) => state.document?.launchAt);

  const [ballisticCoefficient, setBallisticCoefficient] = useState(100);
  const [liftToDragRatio, setLiftToDragRatio] = useState(0);
  const [deorbitDeltaV, setDeorbitDeltaV] = useState(100);
  const reentry = useReentryAnalysis();

  const handleCompute = () => {
    if (!targetOrbit) return;
    reentry.mutate({
      elements: targetOrbit,
      epoch: launchAt ?? new Date().toISOString(),
      ballisticCoefficient,
      liftToDragRatio,
      deorbitDeltaVMetersPerSecond: deorbitDeltaV,
    });
  };

  return (
    <div className="flex flex-col gap-2 rounded-panel border border-border bg-surface-raised p-3 text-xs">
      <h3 className="font-medium text-muted">Reentry prediction</h3>

      {!targetOrbit && <p className="text-faint">Set a target orbit in the Orbit Editor first.</p>}

      <div className="grid grid-cols-3 gap-2">
        <NumberField
          label={<TermHelp termId="ballistic-coefficient">Ballistic coeff. (kg/m²)</TermHelp>}
          value={ballisticCoefficient}
          min={1}
          hint="Higher values generally decelerate deeper in the atmosphere."
          onChange={setBallisticCoefficient}
        />
        <NumberField
          label={<TermHelp termId="lift-to-drag">Lift-to-drag</TermHelp>}
          value={liftToDragRatio}
          min={0}
          max={3}
          hint="Zero models a ballistic entry; lifting vehicles can steer and extend downrange."
          onChange={setLiftToDragRatio}
        />
        <NumberField
          label={<TermHelp termId="delta-v">Deorbit ΔV (m/s)</TermHelp>}
          value={deorbitDeltaV}
          min={0}
          hint="Retrograde velocity change used to lower periapsis into the atmosphere."
          onChange={setDeorbitDeltaV}
        />
      </div>

      <button
        type="button"
        onClick={handleCompute}
        disabled={!targetOrbit || reentry.isPending}
        title="Estimate entry corridor, loads, and landing point without changing the mission"
        className={buttonClass}
      >
        {reentry.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        Predict reentry
      </button>

      {reentry.isError && (
        <p role="alert" className="text-danger">{reentry.error instanceof Error ? reentry.error.message : String(reentry.error)}</p>
      )}

      {reentry.data && (
        <div className="grid grid-cols-2 gap-1 text-muted">
          <span>Landing point</span>
          <span className="text-right text-text">
            {reentry.data.landingPoint.latitudeDeg.toFixed(1)}°, {reentry.data.landingPoint.longitudeDeg.toFixed(1)}°
          </span>
          <span>Downrange</span>
          <span className="text-right text-text">{(reentry.data.downrangeMeters / 1000).toFixed(0)} km</span>
          <span>Flight time</span>
          <span className="text-right text-text">{(reentry.data.flightTimeSeconds / 60).toFixed(1)} min</span>
          <span>Peak deceleration</span>
          <span className="text-right text-text">{reentry.data.peakDecelerationG.toFixed(1)} g</span>
          <span><TermHelp termId="heat-flux">Peak heat flux</TermHelp></span>
          <span className="text-right text-text">{(reentry.data.peakHeatFluxWattsPerSquareMeter / 1e6).toFixed(2)} MW/m²</span>
          <span>Total heat load</span>
          <span className="text-right text-text">{(reentry.data.totalHeatLoadJoulesPerSquareMeter / 1e6).toFixed(1)} MJ/m²</span>
        </div>
      )}
    </div>
  );
}

/**
 * Surfaces the interplanetary/lunar transfer planning and reentry prediction
 * physics in `@trajectory/astro` — real, tested orbital mechanics (see
 * `apps/api/src/routes/analyze.ts`) that otherwise had no UI reachable from
 * the app at all.
 */
export function TransferPlannerPanel() {
  const missionId = useMissionStore((state) => state.id);

  if (!missionId) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-center text-xs text-faint">
        Select a mission first.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-2 overflow-auto p-2 text-sm">
      <PanelLearningBanner panelId="transfer-planner" />
      <TransferSection />
      <ReentrySection />
    </div>
  );
}
